import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { StatusBadge } from "@/pages/Dashboard";
import LiveTrackModal from "@/components/LiveTrackModal";
import RatingsModal from "@/components/RatingsModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star } from "lucide-react";

const empty = {
  company_id: "", customer_id: "", patient_name: "",
  pickup_address: "", dropoff_address: "",
  pickup_datetime: "", appointment_time: "",
  mobility: "ambulatory", trip_type: "one_way",
  payer: "", driver_id: "", vehicle_id: "", notes: "", status: "pending",
  billable_miles: "", fare: "", dropoff_phone: "",
};

const MOBILITY = [
  { value: "ambulatory", label: "Ambulatory" },
  { value: "wheelchair", label: "Wheelchair" },
  { value: "stretcher", label: "Stretcher" },
];
const TRIP = [
  { value: "one_way", label: "One-way" },
  { value: "round_trip", label: "Round-trip" },
];
const STATUS = [
  { value: "pending", label: "Pending" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];
const PAGE_SIZE = 200;
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

export default function Rides() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [offset, setOffset] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);
  const [brokerPo, setBrokerPo] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [liveRideId, setLiveRideId] = useState(null);
  const [playbackRideId, setPlaybackRideId] = useState(null);
  const [ratingsRide, setRatingsRide] = useState(null);

  const isClient = user?.role === "client_user";
  const isAdmin = user?.role === "agency_admin";

  const buildQuery = () => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(offset));
    if (filter !== "all") p.set("status", filter);
    if (showDeleted) p.set("include_deleted", "true");
    if (brokerPo.trim()) p.set("broker_po", brokerPo.trim());
    return p.toString();
  };

  const load = () => api.get(`/rides?${buildQuery()}`).then((r) => setRows(r.data));
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    if (!isClient) {
      api.get("/companies").then((r) => setCompanies(r.data));
      api.get("/drivers").then((r) => setDrivers(r.data));
      api.get("/vehicles").then((r) => setVehicles(r.data));
    }
  }, [isClient]);

  // Debounced so typing in the Broker PO filter doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, offset, showDeleted, brokerPo]);

  const setFilterAndReset = (s) => { setFilter(s); setOffset(0); };
  const setBrokerPoAndReset = (v) => { setBrokerPo(v); setOffset(0); };
  const restore = async (id) => {
    try {
      await api.post(`/rides/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (params.get("new") === "1") {
      setForm(empty); setEditingId(null); setOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
    const live = params.get("live");
    if (live) {
      setLiveRideId(live);
      params.delete("live"); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const hasNext = rows.length === PAGE_SIZE;
  const rangeStart = rows.length === 0 ? 0 : offset + 1;
  const rangeEnd = offset + rows.length;

  const save = async (opts = {}) => {
    try {
      const p = {
        ...form,
        company_id: form.company_id || null,
        customer_id: form.customer_id || null,
        driver_id: form.driver_id || null,
        vehicle_id: form.vehicle_id || null,
        appointment_time: form.appointment_time || null,
        billable_miles: form.billable_miles === "" ? null : Number(form.billable_miles),
        fare: form.fare === "" ? null : Number(form.fare),
      };
      const qs = opts.override ? `?override_compliance=true&override_reason=${encodeURIComponent(opts.reason || "")}` : "";
      if (editingId) await api.put(`/rides/${editingId}${qs}`, p);
      else await api.post(`/rides${qs}`, p);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "driver_non_compliant" && user?.role === "agency_admin") {
        const reason = window.prompt("Driver has compliance gaps. Enter a reason to override (required):");
        if (!reason || !reason.trim()) { toast.error("Override cancelled: a reason is required"); return; }
        return save({ override: true, reason: reason.trim() });
      }
      toast.error(formatApiError(d));
    }
  };

  const quickStatus = async (id, status, opts = {}) => {
    try {
      const qs = opts.override ? `?override_compliance=true&override_reason=${encodeURIComponent(opts.reason || "")}` : "";
      await api.patch(`/rides/${id}/status${qs}`, { status });
      toast.success("Updated");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "driver_non_compliant" && user?.role === "agency_admin") {
        const reason = window.prompt("Driver has compliance gaps. Enter a reason to override (required):");
        if (!reason || !reason.trim()) { toast.error("Override cancelled: a reason is required"); return; }
        return quickStatus(id, status, { override: true, reason: reason.trim() });
      }
      toast.error(formatApiError(d));
    }
  };

  const companyOpts = [{ value: "", label: "— None —" }, ...companies.map((c) => ({ value: c.id, label: c.name }))];
  const customerOpts = [{ value: "", label: "— None —" }, ...customers.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}` }))];
  const driverOpts = [{ value: "", label: "— Unassigned —" }, ...drivers.map((d) => ({ value: d.id, label: d.name }))];
  const vehicleOpts = [{ value: "", label: "— Unassigned —" }, ...vehicles.map((v) => ({ value: v.id, label: `${v.plate} (${v.make} ${v.model})` }))];

  const nameOf = {
    company: (id) => companies.find((c) => c.id === id)?.name,
    driver: (id) => drivers.find((d) => d.id === id)?.name,
    vehicle: (id) => vehicles.find((v) => v.id === id)?.plate,
  };

  return (
    <div data-testid="rides-page">
      <PageHeader
        subtitle="Operations"
        title="Rides"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => { setShowDeleted(e.target.checked); setOffset(0); }} data-testid="rides-show-deleted" />
                Show deleted
              </label>
            )}
            <button onClick={() => { setForm(empty); setEditingId(null); setOpen(true); }} className="btn-primary" data-testid="add-ride-btn">
              + New Ride
            </button>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["all", ...STATUS.map((s) => s.value)].map((s) => (
          <button
            key={s}
            onClick={() => setFilterAndReset(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            data-testid={`filter-${s}`}
          >
            {s === "all" ? "All" : s.replace("_", " ")}
          </button>
        ))}
        <input
          type="text"
          value={brokerPo}
          onChange={(e) => setBrokerPoAndReset(e.target.value)}
          placeholder="Broker PO"
          data-testid="rides-filter-broker-po"
          className="ml-auto text-xs px-3 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 w-40"
        />
      </div>

      <TableShell
        columns={isClient ? ["Patient", "Pickup", "Dropoff", "Pickup Time", "Mobility", "Status", "Track"] : ["Patient", "Company", "Pickup", "Time", "Mobility", "Driver", "Status", "Actions"]}
        empty={rows.length === 0 ? "No rides match this filter." : null}
      >
        {rows.map((r) => {
          const isDeleted = !!r.deleted_at;
          return (
            <tr key={r.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`} data-testid={`ride-row-${r.id}`}>
              <td className="px-4 py-2.5 font-medium">
                {r.patient_name}
                {r.broker_po && (
                  <span
                    className="ml-2 inline-block align-middle font-mono text-[10px] uppercase bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5"
                    title={`Broker: ${r.broker || ""} | PO: ${r.broker_po} | Leg: ${r.broker_leg ?? ""} | Direction: ${r.broker_direction || ""}${r.rider_phone ? ` | Rider phone: ${r.rider_phone}` : ""}`}
                    data-testid={`ride-broker-pill-${r.id}`}
                  >
                    {(r.broker || "").toUpperCase()} {r.broker_po} L{r.broker_leg ?? ""} {(r.broker_direction || "").slice(0, 3)}
                  </span>
                )}
                <div className="text-slate-400 text-[11px] uppercase">{r.source}</div>
                {isDeleted && (
                  <span className="mt-1 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] normal-case">
                    deleted {fmtDeletedDate(r.deleted_at)}
                  </span>
                )}
              </td>
              {!isClient && <td className="px-4 py-2.5 text-slate-600">{nameOf.company(r.company_id) || <span className="text-slate-400">Direct</span>}</td>}
              <td className="px-4 py-2.5 text-slate-600 max-w-[220px] truncate">{r.pickup_address}</td>
              {isClient && <td className="px-4 py-2.5 text-slate-600 max-w-[220px] truncate">{r.dropoff_address}</td>}
              <td className="px-4 py-2.5 font-mono text-xs">{r.pickup_datetime}</td>
              <td className="px-4 py-2.5 capitalize">{r.mobility}</td>
              {!isClient && <td className="px-4 py-2.5 text-slate-600">{nameOf.driver(r.driver_id) || <span className="text-slate-400">-</span>}</td>}
              <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
              {isClient && (
                <td className="px-4 py-2.5">
                  {isDeleted ? (
                    <span className="text-slate-400 text-xs">-</span>
                  ) : r.status === "in_progress" ? (
                    <button className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1" onClick={() => setLiveRideId(r.id)} data-testid={`track-${r.id}`}>
                      <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Track
                    </button>
                  ) : r.status === "completed" ? (
                    <div className="flex items-center gap-2.5">
                      <button className="text-xs text-indigo-700 hover:underline" onClick={() => setPlaybackRideId(r.id)} data-testid={`client-playback-${r.id}`}>Replay</button>
                      <button className="text-xs text-amber-700 hover:underline inline-flex items-center gap-0.5" onClick={() => setRatingsRide(r)} data-testid={`client-ratings-${r.id}`}>
                        <Star size={11} className="mb-0.5" /> Ratings
                      </button>
                    </div>
                  ) : (
                    <span className="text-slate-400 text-xs">-</span>
                  )}
                </td>
              )}
              {!isClient && (
                <td className="px-4 py-2.5 space-x-3 whitespace-nowrap">
                  {isDeleted ? (
                    isAdmin && <button className="text-emerald-700 hover:underline text-xs" onClick={() => restore(r.id)} data-testid={`restore-${r.id}`}>Restore</button>
                  ) : (
                    <>
                      <button className="text-[#005E82] hover:underline text-xs" onClick={() => { setForm({ ...empty, ...r }); setEditingId(r.id); setOpen(true); }} data-testid={`edit-ride-${r.id}`}>Edit</button>
                      {r.status === "pending" && <button className="text-xs text-emerald-700 hover:underline" onClick={() => quickStatus(r.id, "assigned")}>Assign</button>}
                      {r.status === "assigned" && <button className="text-xs text-indigo-700 hover:underline" onClick={() => quickStatus(r.id, "in_progress")}>Start</button>}
                      {r.status === "in_progress" && (
                        <>
                          <button className="text-xs text-emerald-700 hover:underline" onClick={() => quickStatus(r.id, "completed")}>Complete</button>
                          <button className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1" onClick={() => setLiveRideId(r.id)} data-testid={`live-${r.id}`}>
                            <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                          </button>
                        </>
                      )}
                      {r.status === "completed" && (
                        <>
                          <button className="text-xs text-indigo-700 hover:underline" onClick={() => setPlaybackRideId(r.id)} data-testid={`playback-${r.id}`}>Playback</button>
                          <button className="text-xs text-amber-700 hover:underline inline-flex items-center gap-0.5" onClick={() => setRatingsRide(r)} data-testid={`ratings-${r.id}`}>
                            <Star size={11} className="mb-0.5" /> Ratings
                          </button>
                        </>
                      )}
                    </>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </TableShell>

      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-slate-500" data-testid="rides-page-info">
          {rows.length === 0 ? "No rides" : `Showing ${rangeStart}-${rangeEnd}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="rides-prev"
          >
            Prev
          </button>
          <button
            disabled={!hasNext}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="rides-next"
          >
            Next
          </button>
        </div>
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Ride" : "New Ride"} footer={<>
        <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
        <button onClick={save} className="btn-primary" data-testid="save-ride-btn">Save</button>
      </>}>
        <div className="space-y-4">
          {form.broker_po && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600" data-testid="r-broker-info">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Broker</div>
              <div className="font-mono">
                {(form.broker || "").toUpperCase()} | PO: {form.broker_po} | Leg: {form.broker_leg ?? ""} | Direction: {form.broker_direction || ""} | Rider phone: {form.rider_phone || "-"}
              </div>
            </div>
          )}
          <Field label="Patient name" testId="r-patient" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} />
          <Field label="Customer (optional)" testId="r-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} options={customerOpts} />
          {!isClient && <Field label="Client company" testId="r-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} options={companyOpts} />}
          <Field label="Pickup address" testId="r-pickup" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} />
          <Field label="Dropoff address" testId="r-dropoff" value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} />
          {form.dropoff_phone && (
            <div className="text-xs text-slate-600" data-testid="r-dropoff-phone">
              <span className="font-semibold uppercase tracking-[0.1em] text-[10px] text-slate-500 mr-1.5">Destination contact</span>
              <a href={`tel:${form.dropoff_phone}`} className="text-[#005E82] hover:underline">{form.dropoff_phone}</a>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Pickup date/time" testId="r-datetime" type="datetime-local" value={form.pickup_datetime} onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })} />
            <Field label="Appointment time" testId="r-appt" type="datetime-local" value={form.appointment_time} onChange={(e) => setForm({ ...form, appointment_time: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobility" testId="r-mobility" value={form.mobility} onChange={(e) => setForm({ ...form, mobility: e.target.value })} options={MOBILITY} />
            <Field label="Trip type" testId="r-trip" value={form.trip_type} onChange={(e) => setForm({ ...form, trip_type: e.target.value })} options={TRIP} />
          </div>
          <Field label="Payer" testId="r-payer" value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} placeholder="Medicaid / Insurance / Private" />
          {!isClient && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Driver" testId="r-driver" value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })} options={driverOpts} />
              <Field label="Vehicle" testId="r-vehicle" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} options={vehicleOpts} />
            </div>
          )}
          {!isClient && <Field label="Status" testId="r-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUS} />}
          {!isClient && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Billable miles" testId="r-miles" type="number" step="0.1" value={form.billable_miles} onChange={(e) => setForm({ ...form, billable_miles: e.target.value })} />
              <Field label="Fare ($)" testId="r-fare" type="number" step="0.01" value={form.fare} onChange={(e) => setForm({ ...form, fare: e.target.value })} />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300" />
          </div>
        </div>
      </Drawer>
      {liveRideId && <LiveTrackModal rideId={liveRideId} mode="live" onClose={() => setLiveRideId(null)} />}
      {playbackRideId && <LiveTrackModal rideId={playbackRideId} mode="playback" onClose={() => setPlaybackRideId(null)} />}
      {ratingsRide && <RatingsModal rideId={ratingsRide.id} ride={ratingsRide} onClose={() => setRatingsRide(null)} />}
    </div>
  );
}
